import { type FC, memo, useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { type FrameListPlayerViewModel, useFrameViewModel } from 'Lessons';
import { playSound } from 'SoundManager';
import { type FreeResponseFrameViewModel } from './FreeResponseFrameViewModel';
import './locales/free_response/free_response-en.json';
/*
    Most or all of the logic here will likely be moved up to a more reusable component
    if and when we start building more non-iguana frame types.
*/

interface FreeResponseButtonProps {
    playerViewModel: FrameListPlayerViewModel;
    frameViewModel: FreeResponseFrameViewModel;
}

// This is kind of annoying. Adding the disabled property to the button restarts
// the animation. So, we have to remove the animation first before disabling.
// Once the button has been disabled once, we leave the animation disabled until
// it's time to exit.
function useRemoveAnimationThenDisable({
    disableRequested,
    isExiting,
}: {
    disableRequested: boolean;
    isExiting: boolean;
}) {
    const [isDisabled, setIsDisabled] = useState(false);
    const [animationRemoved, setAnimationRemoved] = useState(false);
    useEffect(() => {
        // When disableRequested is set, remove the animation, wait a beat, and then disable
        if (disableRequested && !isExiting) {
            setAnimationRemoved(true);
            requestAnimationFrame(() => {
                setIsDisabled(true);
            });
        }

        // When disableRequested is no longer set, re-enable, but keep the animationRemoved
        // set to true
        if (!disableRequested) {
            setIsDisabled(false);
        }

        // When exiting, bring back the animation
        if (isExiting) {
            setAnimationRemoved(false);
        }
    }, [disableRequested, isExiting]);

    return {
        isDisabled,
        animationRemoved,
    };
}

function Button({
    color,
    buttonLabel,
    handleButtonClick,
    isExiting = false,
    isDisabled: disableRequested,
}: {
    color: 'grey' | 'green';
    buttonLabel: string;
    handleButtonClick: () => void;
    isExiting?: boolean;
    isDisabled: boolean;
}) {
    const { isDisabled, animationRemoved } = useRemoveAnimationThenDisable({ disableRequested, isExiting });

    return (
        <div
            className={clsx(
                'continue-button-wrapper cf-continue-button',
                // Do not allow the skip button to prevent clicks on the continue button
                isExiting ? 'pointer-events-none' : 'pointer-events-auto',
            )}
            data-testid="free-response-continue-button"
        >
            <button
                type="button"
                onClick={handleButtonClick}
                disabled={isDisabled}
                className={clsx(
                    'flat',
                    color,
                    animationRemoved && 'animation-removed',
                    'min-h-[50px] w-full px-5 py-3',
                    'text-base font-semibold uppercase tracking-[0.08em] text-white',
                    'transition-[box-shadow,transform] duration-150 ease-in-out',
                    'sm:rounded-[4px]',
                    'max-sm:rounded-none',
                    isExiting && 'button-exiting',
                )}
            >
                <h2 className="m-0 text-lg leading-tight">{buttonLabel}</h2>
            </button>
        </div>
    );
}

function SkipButton({
    handleButtonClick,
    isExiting,
    isDisabled,
}: {
    handleButtonClick: () => void;
    isExiting: boolean;
    isDisabled: boolean;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'free_response.free_response' });
    const label = t('skip_button');
    return (
        <Button
            color="grey"
            buttonLabel={label}
            handleButtonClick={handleButtonClick}
            isExiting={isExiting}
            isDisabled={isDisabled}
        />
    );
}

function ContinueButton({
    handleButtonClick,
    frameViewModel,
    isDisabled,
}: {
    handleButtonClick: () => void;
    frameViewModel: FreeResponseFrameViewModel;
    isDisabled: boolean;
}) {
    const { t } = useTranslation('back_royal');

    const lesson = frameViewModel.playerViewModel?.lesson;
    const lessonType = lesson?.test ? 'exam' : lesson?.assessment ? 'assessment' : 'lesson';

    const label = t('lessons.lesson.frame_list.frame.componentized.component.continue_button.continue_cta', {
        optionValue: lessonType,
    });
    return <Button color="green" buttonLabel={label} handleButtonClick={handleButtonClick} isDisabled={isDisabled} />;
}

function useHandleButtonClicks({
    playerViewModel,
    frameViewModel,
    actionRequiringConfirmation,
    setActionRequiringConfirmation,
}: {
    playerViewModel: FrameListPlayerViewModel;
    frameViewModel: FreeResponseFrameViewModel;
    actionRequiringConfirmation: 'skip' | 'continue' | null;
    setActionRequiringConfirmation: (action: 'skip' | 'continue' | null) => void;
}) {
    const handleButtonClick = useCallback(
        (action: 'skip' | 'continue') => {
            // Check if this frame is still the active frame (same logic as always_ready_continue_button_dir.js)
            if (playerViewModel.activeFrameId !== frameViewModel.frame.id) {
                return;
            }

            playSound('DEFAULT_CLICK');

            // Skip button always requires confirmation
            if (action === 'skip') {
                if (actionRequiringConfirmation !== 'skip') {
                    setActionRequiringConfirmation('skip');
                    return;
                }
                // Second click: proceed with skip
                setActionRequiringConfirmation(null);
                frameViewModel.completeAndGotoNextFrame();
                return;
            }

            // Continue button requires confirmation if there is an unsent message
            const hasUnsentMessage = frameViewModel.unsentMessageText.trim().length > 0;
            if (hasUnsentMessage) {
                if (actionRequiringConfirmation !== 'continue') {
                    setActionRequiringConfirmation('continue');
                    return;
                }
            }

            // Second click or no unsent message: proceed
            setActionRequiringConfirmation(null);
            frameViewModel?.gotoNextFrame();
        },
        [playerViewModel, frameViewModel, actionRequiringConfirmation, setActionRequiringConfirmation],
    );

    const onSkipClick = useCallback(() => handleButtonClick('skip'), [handleButtonClick]);
    const onContinueClick = useCallback(() => handleButtonClick('continue'), [handleButtonClick]);

    return { onSkipClick, onContinueClick };
}

function useShowConfirmationMessage({
    actionRequiringConfirmation,
    frameViewModel,
}: {
    actionRequiringConfirmation: 'skip' | 'continue' | null;
    frameViewModel: FreeResponseFrameViewModel;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'free_response.free_response' });
    useEffect(() => {
        if (actionRequiringConfirmation === 'skip') {
            const message = t('skip_confirmation');
            frameViewModel.showConfirmationMessage(`<div>${message}</div>`);
        } else if (actionRequiringConfirmation === 'continue') {
            const message = t('continue_confirmation');
            frameViewModel.showConfirmationMessage(`<div>${message}</div>`);
        } else {
            frameViewModel.clearConfirmationMessage();
        }
    }, [actionRequiringConfirmation, frameViewModel, t]);
}

function useHideConfirmationMessageOnOutsideClick({
    actionRequiringConfirmation,
    setActionRequiringConfirmation,
}: {
    actionRequiringConfirmation: 'skip' | 'continue' | null;
    setActionRequiringConfirmation: (action: 'skip' | 'continue' | null) => void;
}) {
    const buttonWrapperRef = useRef<HTMLDivElement>(null);

    // Click outside handler to cancel pending action
    useEffect(() => {
        if (!actionRequiringConfirmation) return undefined;

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (buttonWrapperRef.current && !buttonWrapperRef.current.contains(event.target as Node)) {
                setActionRequiringConfirmation(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [actionRequiringConfirmation, setActionRequiringConfirmation]);

    return buttonWrapperRef;
}

function useDisableButtonsOnConfirmationMessageShown({
    actionRequiringConfirmation,
}: {
    actionRequiringConfirmation: 'skip' | 'continue' | null;
}) {
    const [disableButtons, setDisableButtons] = useState(false);

    useEffect(() => {
        if (!actionRequiringConfirmation) {
            setDisableButtons(false);
            return undefined;
        }

        setDisableButtons(true);
        const timeoutId = window.setTimeout(() => {
            setDisableButtons(false);
        }, 1000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [actionRequiringConfirmation]);

    return disableButtons;
}

const FreeResponseButtonComponent: FC<FreeResponseButtonProps> = ({ playerViewModel, frameViewModel }) => {
    const { complete, showSkipButton } = useFrameViewModel(frameViewModel, vm => ({
        complete: vm.complete,
        showSkipButton: vm.showSkipButton,
    }));

    const [actionRequiringConfirmation, setActionRequiringConfirmation] = useState<'skip' | 'continue' | null>(null);
    const { onSkipClick, onContinueClick } = useHandleButtonClicks({
        playerViewModel,
        frameViewModel,
        actionRequiringConfirmation,
        setActionRequiringConfirmation,
    });
    useShowConfirmationMessage({ actionRequiringConfirmation, frameViewModel });
    const buttonWrapperRef = useHideConfirmationMessageOnOutsideClick({
        actionRequiringConfirmation,
        setActionRequiringConfirmation,
    });
    const disableButtons = useDisableButtonsOnConfirmationMessageShown({ actionRequiringConfirmation });

    // Show button if either complete or skip button should be shown
    if (!complete && !showSkipButton) return null;

    const shouldDisableSkipButton = disableButtons && actionRequiringConfirmation === 'skip';
    const shouldDisableContinueButton = disableButtons && actionRequiringConfirmation === 'continue';

    return (
        <div ref={buttonWrapperRef}>
            {complete && (
                <ContinueButton
                    handleButtonClick={onContinueClick}
                    frameViewModel={frameViewModel}
                    isDisabled={shouldDisableContinueButton}
                />
            )}
            {showSkipButton && (
                <SkipButton handleButtonClick={onSkipClick} isExiting={complete} isDisabled={shouldDisableSkipButton} />
            )}
        </div>
    );
};

export const FreeResponseButton = memo(FreeResponseButtonComponent) as typeof FreeResponseButtonComponent;
